import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useRenameSpace } from '@/hooks/useAdmin';
import type { Container } from '@/types/domain';

const useStyles = makeStyles({
  content: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
});

interface Props {
  space: Container;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Rename dialog for a team space. Renaming changes only the local label shown in
// the add-in; the underlying Microsoft 365 group is untouched.
export function RenameSpaceDialog({ space, open, onOpenChange }: Props) {
  const styles = useStyles();
  const [name, setName] = useState(space.name);
  const rename = useRenameSpace();

  const trimmed = name.trim();
  const errMsg = rename.error instanceof Error ? rename.error.message : null;
  // Nothing to do if the field is empty or unchanged.
  const canSubmit = !rename.isPending && trimmed.length > 0 && trimmed !== space.name;

  const submit = () => {
    if (!canSubmit) return;
    rename.mutate({ containerId: space.id, name: trimmed }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={(_e, d) => onOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Renommer l'espace</DialogTitle>
          <DialogContent className={styles.content}>
            <Field label="Nom de l'espace">
              <Input
                value={name}
                autoFocus
                maxLength={255}
                onChange={(_e, d) => setName(d.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
            </Field>
            {errMsg && (
              <MessageBar intent="error">
                <MessageBarBody>{errMsg}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)} disabled={rename.isPending}>
              Annuler
            </Button>
            <Button appearance="primary" onClick={submit} disabled={!canSubmit}>
              {rename.isPending ? 'Enregistrement…' : 'Renommer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
